import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { BarChart3, Download, Users, BookOpen, FileBadge, Gamepad2, Trophy, ClipboardList, Video, ListChecks } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend, PieChart, Pie, Cell } from 'recharts';

const TABS = [
    { key: 'learner', label: 'Learner Progress', icon: Users },
    { key: 'course', label: 'Course Completion', icon: BookOpen },
    { key: 'video', label: 'Video Report', icon: Video },
    { key: 'quiz', label: 'Quiz Report', icon: ListChecks },
    { key: 'cert', label: 'Certifications', icon: FileBadge },
    { key: 'game', label: 'Game Engagement', icon: Gamepad2 },
    { key: 'leaderboard', label: 'Leaderboard', icon: Trophy },
    { key: 'all', label: 'All Learners', icon: ClipboardList },
];

export default function AdminReports() {
    const [active, setActive] = useState('learner');
    const [filters, setFilters] = useState({ department: '', region: '', role: '', days: '', date_from: '', date_to: '', course_id: '', language: '' });
    const [filterOpts, setFilterOpts] = useState({ departments: [], regions: [], roles: [], courses: [], languages: [] });

    useEffect(() => { api.get('/admin/filters/options').then((r) => setFilterOpts(r.data)); }, []);

    return (
        <div data-testid="admin-reports-page">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red">Insights</div>
                    <h1 className="font-display font-extrabold text-3xl text-slate-900 mt-1 flex items-center gap-2"><BarChart3 className="h-7 w-7 text-brand-black" /> Reports</h1>
                    <p className="text-sm text-slate-500 mt-1">Multi-dimensional reports. Filter, sort and export to CSV.</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-2xl border border-slate-200 p-1 inline-flex mb-4" data-testid="reports-tabs">
                {TABS.map((t) => (
                    <button
                        key={t.key} onClick={() => setActive(t.key)}
                        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition ${active === t.key ? 'bg-brand-black text-brand-yellow' : 'text-slate-600 hover:text-slate-900'}`}
                        data-testid={`reports-tab-${t.key}`}
                    >
                        <t.icon className="h-4 w-4" /> {t.label}
                    </button>
                ))}
            </div>

            {/* Filter bar */}
            <div className="bg-white rounded-2xl border border-slate-200 p-3 flex flex-wrap gap-2 mb-5">
                <Sel label="Department" v={filters.department} on={(v) => setFilters({ ...filters, department: v })} options={filterOpts.departments} t="report-dept-filter" />
                <Sel label="Region" v={filters.region} on={(v) => setFilters({ ...filters, region: v })} options={filterOpts.regions} t="report-region-filter" />
                {active === 'learner' && (
                    <Sel label="Role" v={filters.role} on={(v) => setFilters({ ...filters, role: v })} options={filterOpts.roles} t="report-role-filter" />
                )}
                {active === 'game' && (
                    <Sel label="Window" v={filters.days} on={(v) => setFilters({ ...filters, days: v })} options={['7', '30', '90']} t="report-days-filter" />
                )}
                {(active === 'learner' || active === 'course' || active === 'all') && (
                    <>
                        <select value={filters.course_id} onChange={(e) => setFilters({ ...filters, course_id: e.target.value })}
                            className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white max-w-[240px] truncate" data-testid="report-course-filter">
                            <option value="">All Courses</option>
                            {(filterOpts.courses || []).filter((c) => !filters.language || c.language === filters.language).map((c) => <option key={c.id} value={c.id}>{c.title} ({(c.language || 'en').toUpperCase()})</option>)}
                        </select>
                        <Sel label="Language" v={filters.language} on={(v) => setFilters({ ...filters, language: v, course_id: '' })} options={filterOpts.languages} t="report-language-filter" />
                    </>
                )}
                {(active === 'video' || active === 'quiz') && (
                    <Sel label="Language" v={filters.language} on={(v) => setFilters({ ...filters, language: v })} options={filterOpts.languages} t="report-language-filter" />
                )}
                <div className="flex items-center gap-1.5" data-testid="report-date-range">
                    <label className="text-xs font-semibold text-slate-500">From</label>
                    <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                        className="px-2.5 py-2 rounded-xl border border-slate-200 text-sm bg-white" data-testid="report-date-from" />
                    <label className="text-xs font-semibold text-slate-500">To</label>
                    <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                        className="px-2.5 py-2 rounded-xl border border-slate-200 text-sm bg-white" data-testid="report-date-to" />
                </div>
                <button onClick={() => setFilters({ department: '', region: '', role: '', days: '', date_from: '', date_to: '', course_id: '', language: '' })} className="text-xs text-slate-600 hover:text-slate-900 ml-1" data-testid="reports-clear-btn">Clear filters</button>
            </div>

            {active === 'learner' && <LearnerProgressReport filters={filters} />}
            {active === 'course' && <CourseCompletionReport filters={filters} />}
            {active === 'video' && <VideoSummaryReport filters={filters} />}
            {active === 'quiz' && <QuizSummaryReport filters={filters} />}
            {active === 'cert' && <CertReport filters={filters} />}
            {active === 'game' && <GameReport filters={filters} />}
            {active === 'leaderboard' && <LeaderboardReport filters={filters} />}
            {active === 'all' && <AllLearnersReport filters={filters} />}
        </div>
    );
}

function Sel({ label, v, on, options, t }) {
    return (
        <select value={v} onChange={(e) => on(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white capitalize" data-testid={t}>
            <option value="">All {label}</option>
            {(options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
    );
}

function withDates(params, filters) {
    if (filters?.date_from) params.date_from = filters.date_from;
    if (filters?.date_to) params.date_to = filters.date_to;
    return params;
}

function fmtDate(s) {
    if (!s) return '—';
    try { return new Date(s).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return s; }
}

function downloadCSV(rows, columns, filename) {    const header = columns.map((c) => c.label).join(',') + '\n';
    const body = rows.map((r) => columns.map((c) => `"${String(c.render ? c.render(r) : (r[c.key] ?? '')).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([header + body], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
}

function ReportTable({ rows, columns, filename, testid }) {
    return (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={testid}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-600">{rows.length} record{rows.length === 1 ? '' : 's'}</div>
                <button onClick={() => downloadCSV(rows, columns, filename)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-brand-black text-brand-yellow text-xs font-semibold" data-testid="report-export-btn">
                    <Download className="h-3.5 w-3.5" /> Export CSV
                </button>
            </div>
            <div className="overflow-auto max-h-[60vh]">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0 text-xs uppercase tracking-wider text-slate-600">
                        <tr>{columns.map((c) => <th key={c.key} className={`py-3 px-4 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.label}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {rows.map((r, i) => (
                            <tr key={r.user_id || r.course || r.certificate_id || r.game || i} className="hover:bg-slate-50">
                                {columns.map((c) => <td key={c.key} className={`py-2.5 px-4 ${c.align === 'right' ? 'text-right' : ''}`}>{c.render ? c.render(r) : (r[c.key] ?? '—')}</td>)}
                            </tr>
                        ))}
                        {!rows.length && <tr><td colSpan={columns.length} className="py-12 text-center text-sm text-slate-500">No data for current filters.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function LearnerProgressReport({ filters }) {
    const [rows, setRows] = useState([]);
    useEffect(() => {
        const params = {};
        if (filters.department) params.department = filters.department;
        if (filters.region) params.region = filters.region;
        if (filters.role) params.role = filters.role;
        if (filters.course_id) params.course_id = filters.course_id;
        if (filters.language) params.language = filters.language;
        withDates(params, filters);
        api.get('/admin/reports/learner-progress', { params }).then((r) => setRows(r.data.rows || []));
    }, [filters]);

    const cols = [
        { key: 'full_name', label: 'Learner', render: (r) => (
            <Link to={`/admin/learners/${r.user_id}`} className="font-semibold text-brand-blue hover:underline" data-testid={`report-learner-link-${r.user_id}`}>{r.full_name}</Link>
        ) },
        { key: 'mobile', label: 'Mobile' },
        { key: 'department', label: 'Department' },
        { key: 'region', label: 'Region' },
        { key: 'enrollments', label: 'Started', align: 'right' },
        { key: 'in_progress', label: 'In Progress', align: 'right' },
        { key: 'completed', label: 'Completed', align: 'right' },
        { key: 'certificates', label: 'Certificates', align: 'right' },
        { key: 'avg_progress', label: 'Avg Progress', align: 'right', render: (r) => `${r.avg_progress}%` },
        { key: 'points', label: 'Points', align: 'right' },
    ];
    return (
        <div className="space-y-5">
            <div className="grid lg:grid-cols-2 gap-5">
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                    <h3 className="font-display font-bold mb-3 text-slate-900">Top 10 by avg progress</h3>
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={rows.slice(0, 10)} layout="vertical" margin={{ left: 30 }}>
                            <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                            <XAxis type="number" tick={{ fontSize: 11 }} />
                            <YAxis type="category" dataKey="full_name" tick={{ fontSize: 11 }} width={120} />
                            <Tooltip />
                            <Bar dataKey="avg_progress" fill="#231F20" radius={[0, 8, 8, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                    <h3 className="font-display font-bold mb-3 text-slate-900">Points distribution (top 10)</h3>
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={rows.slice(0, 10).sort((a, b) => b.points - a.points)} margin={{ bottom: 10 }}>
                            <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                            <XAxis dataKey="full_name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={70} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Bar dataKey="points" fill="#FFF200" stroke="#231F20" radius={[8, 8, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
            <ReportTable rows={rows} columns={cols} filename="learner-progress.csv" testid="learner-progress-table" />
        </div>
    );
}

function CourseCompletionReport({ filters }) {
    const [rows, setRows] = useState([]);
    useEffect(() => {
        const params = withDates({}, filters);
        if (filters.course_id) params.course_id = filters.course_id;
        if (filters.language) params.language = filters.language;
        api.get('/admin/reports/course-completion', { params }).then((r) => setRows(r.data || []));
    }, [filters]);
    const cols = [
        { key: 'course', label: 'Course' },
        { key: 'language', label: 'Lang', render: (r) => (r.language || 'en').toUpperCase() },
        { key: 'total', label: 'Learners Started', align: 'right' },
        { key: 'completed', label: 'Completed', align: 'right' },
        { key: 'certificates', label: 'Certificates', align: 'right' },
        { key: 'avg_progress', label: 'Avg Progress', align: 'right', render: (r) => `${r.avg_progress}%` },
        { key: 'completion_rate', label: 'Completion Rate', align: 'right', render: (r) => `${r.completion_rate}%` },
    ];
    return (
        <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h3 className="font-display font-bold mb-3 text-slate-900">Completion rate by course</h3>
                <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={rows}>
                        <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                        <XAxis dataKey="course" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={70} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="total" fill="#231F20" name="Started" radius={[8, 8, 0, 0]} />
                        <Bar dataKey="completed" fill="#FFF200" stroke="#231F20" name="Completed" radius={[8, 8, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <ReportTable rows={rows} columns={cols} filename="course-completion.csv" testid="course-completion-table" />
        </div>
    );
}

function VideoSummaryReport({ filters }) {
    // Point 52 (VIDEO only): Course ID · Title · started · in progress · completed
    const [rows, setRows] = useState([]);
    useEffect(() => {
        const params = {};
        if (filters.language) params.language = filters.language;
        api.get('/admin/reports/video-summary', { params }).then((r) => setRows(r.data || []));
    }, [filters]);
    const cols = [
        { key: 'course_id', label: 'Course ID' },
        { key: 'course_title', label: 'Course Title' },
        { key: 'language', label: 'Lang', render: (r) => (r.language || 'en').toUpperCase() },
        { key: 'started', label: 'Total User Started', align: 'right' },
        { key: 'in_progress', label: 'In Progress', align: 'right' },
        { key: 'completed', label: 'Total User Completed', align: 'right' },
    ];
    return (
        <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h3 className="font-display font-bold mb-3 text-slate-900">Video engagement by course</h3>
                <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={rows}>
                        <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                        <XAxis dataKey="course_title" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={70} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="started" fill="#231F20" name="Started" radius={[8, 8, 0, 0]} />
                        <Bar dataKey="in_progress" fill="#94A3B8" name="In Progress" radius={[8, 8, 0, 0]} />
                        <Bar dataKey="completed" fill="#FFF200" stroke="#231F20" name="Completed" radius={[8, 8, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <ReportTable rows={rows} columns={cols} filename="video-report.csv" testid="video-summary-table" />
        </div>
    );
}

function QuizSummaryReport({ filters }) {
    // Point 52 (QUIZ only): Course ID · Title · started quiz · in progress quiz · passed · failed
    const [rows, setRows] = useState([]);
    useEffect(() => {
        const params = {};
        if (filters.language) params.language = filters.language;
        api.get('/admin/reports/quiz-summary', { params }).then((r) => setRows(r.data || []));
    }, [filters]);
    const cols = [
        { key: 'course_id', label: 'Course ID' },
        { key: 'course_title', label: 'Course Title' },
        { key: 'language', label: 'Lang', render: (r) => (r.language || 'en').toUpperCase() },
        { key: 'started_quiz', label: 'Total Started Quiz', align: 'right' },
        { key: 'in_progress_quiz', label: 'In Progress Quiz', align: 'right' },
        { key: 'passed', label: 'Total Passed Quiz', align: 'right' },
        { key: 'failed', label: 'Total Failed Quiz', align: 'right' },
    ];
    return (
        <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h3 className="font-display font-bold mb-1 text-slate-900">Quiz outcomes by course</h3>
                <p className="text-xs text-slate-500 mb-3">In-progress quiz = learners who started the course but haven't attempted the quiz yet.</p>
                <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={rows}>
                        <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                        <XAxis dataKey="course_title" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={70} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="passed" fill="#22C55E" name="Passed" radius={[8, 8, 0, 0]} />
                        <Bar dataKey="failed" fill="#EF4444" name="Failed" radius={[8, 8, 0, 0]} />
                        <Bar dataKey="in_progress_quiz" fill="#94A3B8" name="In Progress" radius={[8, 8, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <ReportTable rows={rows} columns={cols} filename="quiz-report.csv" testid="quiz-summary-table" />
        </div>
    );
}

function CertReport({ filters }) {
    const [rows, setRows] = useState([]);
    useEffect(() => {
        const params = {};
        if (filters.department) params.department = filters.department;
        if (filters.region) params.region = filters.region;
        withDates(params, filters);
        api.get('/admin/reports/certifications', { params }).then((r) => setRows(r.data.rows || []));
    }, [filters]);
    const cols = [
        { key: 'course', label: 'Course' },
        { key: 'count', label: 'Certifications', align: 'right' },
        { key: 'avg_score', label: 'Avg Score', align: 'right', render: (r) => `${r.avg_score}%` },
    ];
    return (
        <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h3 className="font-display font-bold mb-3 text-slate-900">Certifications by course</h3>
                <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                        <Pie data={rows} dataKey="count" nameKey="course" cx="50%" cy="50%" outerRadius={90} innerRadius={45} paddingAngle={4}>
                            {rows.map((_, i) => <Cell key={i} fill={['#231F20', '#FFF200', '#E31E24', '#F47E1F', '#8B5CF6'][i % 5]} stroke="#fff" />)}
                        </Pie>
                        <Tooltip />
                    </PieChart>
                </ResponsiveContainer>
            </div>
            <ReportTable rows={rows} columns={cols} filename="certifications.csv" testid="cert-table" />
        </div>
    );
}

function GameReport({ filters }) {
    const [rows, setRows] = useState([]);
    useEffect(() => {
        const params = { days: filters.days || '30' };
        withDates(params, filters);
        api.get('/admin/reports/game-engagement', { params }).then((r) => setRows(r.data.rows || []));
    }, [filters]);
    const cols = [
        { key: 'game', label: 'Game', render: (r) => (
            <Link to={`/admin/reports/game/${r.game}`} className="font-semibold text-brand-blue hover:underline" data-testid={`report-game-link-${r.game}`}>{r.game}</Link>
        ) },
        { key: 'plays', label: 'Plays', align: 'right' },
        { key: 'unique_players', label: 'Unique Players', align: 'right' },
        { key: 'avg_score', label: 'Avg Score', align: 'right' },
        { key: 'total_points', label: 'Points Distributed', align: 'right' },
    ];
    return (
        <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h3 className="font-display font-bold mb-3 text-slate-900">Plays per game (last {filters.days || 30} days)</h3>
                <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={rows}>
                        <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                        <XAxis dataKey="game" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="plays" fill="#231F20" radius={[8, 8, 0, 0]} />
                        <Bar dataKey="unique_players" fill="#FFF200" stroke="#231F20" radius={[8, 8, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <ReportTable rows={rows} columns={cols} filename="game-engagement.csv" testid="game-engagement-table" />
        </div>
    );
}


function LeaderboardReport({ filters }) {
    const [rows, setRows] = useState([]);
    useEffect(() => {
        api.get('/admin/reports/leaderboard', { params: withDates({ limit: 100 }, filters) }).then((r) => {
            const ranked = (r.data.rows || []).map((u, i) => ({ ...u, rank: i + 1 }));
            setRows(ranked);
        });
    }, [filters]);
    const cols = [
        { key: 'rank', label: '#', render: (r) => (
            <span className={`inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded-full text-xs font-bold ${r.rank <= 3 ? 'bg-brand-yellow text-brand-black' : 'bg-slate-100 text-slate-600'}`}>{r.rank}</span>
        ) },
        { key: 'name', label: 'Learner', render: (r) => (
            r.id
                ? <Link to={`/admin/learners/${r.id}`} className="font-semibold text-brand-blue hover:underline" data-testid={`report-leader-link-${r.id}`}>{r.full_name || r.name || '—'}</Link>
                : (r.full_name || r.name || '—')
        ) },
        { key: 'department', label: 'Department' },
        { key: 'region', label: 'Region' },
        { key: 'points', label: 'Points', align: 'right', render: (r) => <span className="font-bold text-slate-900">{(r.points ?? 0).toLocaleString()}</span> },
    ];
    return (
        <div className="space-y-5" data-testid="leaderboard-report">
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h3 className="font-display font-bold mb-3 text-slate-900 flex items-center gap-2"><Trophy className="h-5 w-5 text-brand-yellowDark" /> Top learners by points</h3>
                <p className="text-sm text-slate-500">Live ranking of trainees across the academy. Top 3 are highlighted.</p>
            </div>
            <ReportTable rows={rows} columns={cols} filename="leaderboard.csv" testid="leaderboard-table" />
        </div>
    );
}

function AllLearnersReport({ filters }) {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [exporting, setExporting] = useState(false);

    const buildParams = (limit) => {
        const params = { limit };
        if (filters.department) params.department = filters.department;
        if (filters.region) params.region = filters.region;
        if (filters.course_id) params.course_id = filters.course_id;
        if (filters.language) params.language = filters.language;
        withDates(params, filters);
        return params;
    };

    useEffect(() => {
        api.get('/admin/reports/all-learners', { params: buildParams(100) }).then((r) => {
            setRows(r.data.rows || []);
            setTotal(r.data.total || 0);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const cols = [
        { key: 'course_code', label: 'Course ID' },
        { key: 'learner_name', label: 'Learner' },
        { key: 'mobile', label: 'Mobile' },
        { key: 'course_name', label: 'Course' },
        { key: 'date', label: 'Start Date', render: (r) => fmtDate(r.date) },
        { key: 'language', label: 'Lang', render: (r) => (r.language || 'en').toUpperCase() },
        { key: 'department', label: 'Department' },
        { key: 'region', label: 'Region' },
        { key: 'started', label: 'Started', align: 'center' },
        { key: 'in_progress', label: 'In Progress', align: 'center' },
        { key: 'completed', label: 'Completed', align: 'center' },
        { key: 'certificate', label: 'Certificate', align: 'center' },
        { key: 'scored_pct', label: 'Scored %', align: 'right', render: (r) => `${r.scored_pct}%` },
        { key: 'avg_progress', label: 'Avg Progress', align: 'right', render: (r) => `${r.avg_progress}%` },
        { key: 'points_scored', label: 'Points Scored', align: 'right' },
        { key: 'balance_points', label: 'Balance Points', align: 'right' },
    ];

    const exportAll = async () => {
        setExporting(true);
        try {
            const r = await api.get('/admin/reports/all-learners', { params: buildParams(0) });
            downloadCSV(r.data.rows || [], cols, 'all-learners.csv');
        } finally { setExporting(false); }
    };

    return (
        <div className="space-y-4" data-testid="all-learners-report">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-100">
                    <div className="text-xs font-bold uppercase tracking-wide text-slate-600" data-testid="all-learners-count">
                        Showing {rows.length} of {total} · export includes all {total}
                    </div>
                    <button onClick={exportAll} disabled={exporting}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-brand-black text-brand-yellow text-xs font-semibold disabled:opacity-60"
                        data-testid="all-learners-export-btn">
                        <Download className="h-3.5 w-3.5" /> {exporting ? 'Preparing…' : 'Export CSV (all)'}
                    </button>
                </div>
                <div className="overflow-auto max-h-[68vh]">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0 text-[11px] uppercase tracking-wider text-slate-600">
                            <tr>{cols.map((c) => <th key={c.key} className={`py-3 px-3 whitespace-nowrap ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : 'text-left'}`}>{c.label}</th>)}</tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {rows.map((r, i) => (
                                <tr key={`${r.user_id}-${r.course_code}-${i}`} className="hover:bg-slate-50" data-testid={`all-learner-row-${i}`}>
                                    {cols.map((c) => <td key={c.key} className={`py-2.5 px-3 whitespace-nowrap ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : ''}`}>{c.render ? c.render(r) : (r[c.key] ?? '—')}</td>)}
                                </tr>
                            ))}
                            {!rows.length && <tr><td colSpan={cols.length} className="py-12 text-center text-sm text-slate-500">No data for current filters.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
